/**
 * AI CONTEXT
 * Purpose: evaluation debugging/admin tooling page (keywords, moments, reset ops).
 * Key deps: Jotai doc subscriptions, Firebase admin actions, EvaluationFormModal.
 * Referenced by: App route /eval-debug (debug-gated).
 * Importance: troubleshooting and migration validation for grading data.
 * Refactor: move destructive ops behind server-protected endpoints/guardrails.
 */
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DatabaseOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  FormOutlined,
} from "@ant-design/icons"
import { App, Button, Card, Col, Divider, Input, Row, Typography } from "antd"
import { useCallback, useMemo, useState } from "react"

import {
  classAtoms,
  evaluationAtoms,
  evaluationKeywordAtoms,
  standoutMomentAtoms,
  studentAtoms,
} from "#atoms"
import Flex from "#blocks/Flex"
import Firebase, { NULL_ID } from "#classes/Firebase"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { EvaluationConstructor } from "#customTypes/composedTypes/evaluationTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  EvaluationKeywordConstructor,
  StandoutMomentConstructor,
} from "#customTypes/composedTypes/standoutTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useFilteredDocs, useOneDoc } from "#hooks/jotai/useSubscription"
import DataDebugger from "#mocks/DataDebugger"
import { generateEvalId } from "#utils/evaluationHelpers"

import EvaluationFormModal from "./EvaluationView/EvaluationFormModal"
import { EvaluationFormContextProvider } from "./EvaluationView/useEvaluationFormContext"
import StandoutMomentPanel from "./TeacherView/StandoutMomentPanel"

const SEED_KEYWORDS = [
  "Creativity",
  "Imagination",
  "Craftsmanship",
  "Technique",
  "Composition",
  "Color Use",
  "Detail",
  "Observation",
  "Experimentation",
  "Effort",
  "Focus",
  "Persistence",
  "Initiative",
  "Independence",
  "Planning",
  "Reflection",
  "Practice",
  "Revision",
  "Curiosity",
  "Critical Thinking",
  "Problem Solving",
  "Communication",
  "Collaboration",
  "Leadership",
  "Participation",
  "Respect",
  "Encouragement",
  "Responsibility",
] as const

// Prod
const DEFAULT_STUDENT_ID = "sT1mXjJEOgBYgrcnWYmh"
const DEFAULT_CLASS_ID = "OyrtU2tserr56fCZj4dP"

// Demo
// const DEFAULT_STUDENT_ID = "qh5RC8zUa17nlfKLgy41"
// const DEFAULT_CLASS_ID = "XRsJaGrt4IFIV7SOdwhU"

const { Title, Text } = Typography

const SaveStatus = ({ label, exists }: { label: string; exists: boolean }) => {
  const theme = useThemeContext()
  return (
    <Flex gap={8} style={{ alignItems: "center" }}>
      {exists ? (
        <CheckCircleFilled style={{ color: theme.eric.green, fontSize: 18 }} />
      ) : (
        <CloseCircleFilled style={{ color: theme.eric.red, fontSize: 18 }} />
      )}
      <Text strong>{label}</Text>
    </Flex>
  )
}

const EvalDebugDashboard = () => {
  const { message, modal } = App.useApp()
  const theme = useThemeContext()
  const { currentUserId } = useUserContext()

  const [studentIdInput, setStudentIdInput] = useState(DEFAULT_STUDENT_ID)
  const [classIdInput, setClassIdInput] = useState(DEFAULT_CLASS_ID)
  const [activeStudentId, setActiveStudentId] = useState<Uuid | null>(null)
  const [activeClassId, setActiveClassId] = useState<Uuid | null>(null)
  const [showEvalModal, setShowEvalModal] = useState(false)
  const [isSeeding, setIsSeeding] = useState(false)
  const [isResetting, setIsResetting] = useState(false)

  const handleLoad = () => {
    const sid = studentIdInput.trim()
    const cid = classIdInput.trim()
    if (!sid || !cid) {
      void message.warning("Both Student ID and Class ID are required")
      return
    }
    setActiveStudentId(sid)
    setActiveClassId(cid)
  }

  const student = useOneDoc<StudentConstructor>(
    studentAtoms,
    DOC_TYPES.STUDENTS,
    activeStudentId || NULL_ID
  )

  const classDoc = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    activeClassId || NULL_ID
  )

  const evaluationId = useMemo(() => {
    if (!activeStudentId || !classDoc) return null
    return generateEvalId(activeStudentId, classDoc.semester, classDoc.group)
  }, [activeStudentId, classDoc])

  const evaluation = useOneDoc<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    evaluationId || NULL_ID
  )

  const momentQuery = useMemo(
    () => ({
      queries: {
        studentId: {
          value: activeStudentId || NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
        semester: {
          value: classDoc?.semester || NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
        curriculumId: {
          value: classDoc?.curriculumId || NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [activeStudentId, classDoc?.semester, classDoc?.curriculumId]
  )

  const moments = useFilteredDocs<StandoutMomentConstructor>(
    standoutMomentAtoms,
    DOC_TYPES.STANDOUT_MOMENTS,
    momentQuery
  )

  const tagIdsFromMoments = useMemo(() => {
    const ids = new Set<Uuid>()
    for (const m of moments) {
      for (const id of m.tagIds ?? []) ids.add(id)
    }
    if (evaluation?.standoutSummary) {
      for (const entry of Object.values(evaluation.standoutSummary)) {
        if (entry?.tagIds) {
          for (const id of entry.tagIds) ids.add(id)
        }
      }
    }
    return Array.from(ids)
  }, [moments, evaluation?.standoutSummary])

  const tagQuery = useMemo(
    () => ({
      queries: {
        isActive: { value: true, comparator: COMPARATORS.EQUALS },
      },
    }),
    []
  )

  const allTags = useFilteredDocs<EvaluationKeywordConstructor>(
    evaluationKeywordAtoms,
    DOC_TYPES.EVALUATION_KEYWORDS,
    tagQuery
  )

  const referencedTags = useMemo(() => {
    return allTags.filter((t) => tagIdsFromMoments.includes(t.id))
  }, [allTags, tagIdsFromMoments])

  const handleSeedKeywords = useCallback(async () => {
    if (!currentUserId) return

    setIsSeeding(true)
    try {
      const fb = Firebase.getInstance()
      const existingTexts = new Set(
        allTags.map((t) => t.text.trim().toLowerCase())
      )
      const now = new Date().toISOString()
      let created = 0

      for (const text of SEED_KEYWORDS) {
        if (existingTexts.has(text.toLowerCase())) continue

        await fb.addDocument<EvaluationKeywordConstructor>(
          DOC_TYPES.EVALUATION_KEYWORDS,
          {
            pool: "skill",
            text,
            isSeeded: true,
            isActive: true,
            createdBy: currentUserId,
            createdAt: now,
          }
        )
        created++
      }

      void message.success(
        created > 0
          ? `Seeded ${created} keywords (${SEED_KEYWORDS.length - created} already existed)`
          : "All keywords already exist"
      )
    } catch (e) {
      void message.error(`Seed failed: ${String(e)}`)
    } finally {
      setIsSeeding(false)
    }
  }, [currentUserId, allTags, message])

  const handleResetGradingData = useCallback(async () => {
    modal.confirm({
      title: "Reset All Grading Data?",
      content:
        "This will DELETE all standout moments and CLEAR standoutSummary from all evaluations. This cannot be undone.",
      okText: "Yes, Reset Everything",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setIsResetting(true)
        try {
          const fb = Firebase.getInstance()

          const allMoments = await fb.variableQuery<StandoutMomentConstructor>(
            DOC_TYPES.STANDOUT_MOMENTS,
            {}
          )
          for (const m of allMoments) {
            await fb.deleteDocument(DOC_TYPES.STANDOUT_MOMENTS, m.id)
          }

          const allEvals = await fb.variableQuery<EvaluationConstructor>(
            DOC_TYPES.EVALUATIONS,
            {}
          )
          const evalsWithSummary = allEvals.filter(
            (e) =>
              e.standoutSummary && Object.keys(e.standoutSummary).length > 0
          )
          for (const e of evalsWithSummary) {
            await fb.updateDocument(DOC_TYPES.EVALUATIONS, e.id, {
              standoutSummary: {},
            })
          }

          void message.success(
            `Deleted ${allMoments.length} moments, cleared summary from ${evalsWithSummary.length} evaluations`
          )
        } catch (e) {
          void message.error(`Reset failed: ${String(e)}`)
        } finally {
          setIsResetting(false)
        }
      },
    })
  }, [modal, message])

  const hasData = activeStudentId && activeClassId
  const studentName = student
    ? `${student.firstName} ${student.lastName}`
    : activeStudentId || ""

  return (
    <Flex
      col
      gap={16}
      style={{ padding: 40, maxWidth: 1400, margin: "0 auto" }}
    >
      <Title level={3}>
        <ExperimentOutlined /> Eval Debug Dashboard
      </Title>

      <Card title="Keyword Admin">
        <Flex gap={12} style={{ flexWrap: "wrap" }}>
          <Button
            icon={<DatabaseOutlined />}
            onClick={handleSeedKeywords}
            loading={isSeeding}
          >
            Seed {SEED_KEYWORDS.length} Keywords
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleResetGradingData}
            loading={isResetting}
          >
            Reset All Grading Data
          </Button>
          <Text type="secondary" style={{ alignSelf: "center", fontSize: 12 }}>
            {allTags.length} keywords in DB
          </Text>
        </Flex>
      </Card>

      <Card>
        <Flex gap={12} style={{ alignItems: "flex-end" }}>
          <Flex col gap={4} style={{ flex: 1 }}>
            <Text strong>Student ID</Text>
            <Input
              value={studentIdInput}
              onChange={(e) => setStudentIdInput(e.target.value)}
              placeholder="Paste student ID..."
              onPressEnter={handleLoad}
            />
          </Flex>
          <Flex col gap={4} style={{ flex: 1 }}>
            <Text strong>Class ID</Text>
            <Input
              value={classIdInput}
              onChange={(e) => setClassIdInput(e.target.value)}
              placeholder="Paste class ID..."
              onPressEnter={handleLoad}
            />
          </Flex>
          <Button type="primary" onClick={handleLoad}>
            Load
          </Button>
        </Flex>
      </Card>

      {hasData && (
        <>
          {/* Status indicators */}
          <Card title="Save Status">
            <Flex gap={24} style={{ flexWrap: "wrap" }}>
              <SaveStatus label="Student found" exists={!!student} />
              <SaveStatus label="Class found" exists={!!classDoc} />
              <SaveStatus label="Evaluation exists" exists={!!evaluation} />
              <SaveStatus
                label="standoutSummary saved"
                exists={
                  !!evaluation?.standoutSummary &&
                  Object.keys(evaluation.standoutSummary).length > 0
                }
              />
              <SaveStatus
                label={`Standout moments (${moments.length})`}
                exists={moments.length > 0}
              />
              <SaveStatus
                label={`Referenced tags (${referencedTags.length})`}
                exists={referencedTags.length > 0}
              />
            </Flex>
          </Card>

          {/* Actions */}
          <Card title="Actions">
            <Flex gap={12}>
              {evaluationId && (
                <Button
                  type="primary"
                  icon={<FormOutlined />}
                  onClick={() => setShowEvalModal(true)}
                  style={{ backgroundColor: theme.eric.blue }}
                >
                  Open Evaluation Form
                </Button>
              )}
            </Flex>
          </Card>

          <Row gutter={16}>
            {/* Left column: data views */}
            <Col span={14}>
              <Flex col gap={16}>
                <Card
                  title="Student"
                  size="small"
                  styles={{ body: { padding: 0 } }}
                >
                  {student ? (
                    <DataDebugger data={student} />
                  ) : (
                    <Text type="secondary" style={{ padding: 16 }}>
                      Not found
                    </Text>
                  )}
                </Card>

                <Card
                  title="Class"
                  size="small"
                  styles={{ body: { padding: 0 } }}
                >
                  {classDoc ? (
                    <DataDebugger data={classDoc} />
                  ) : (
                    <Text type="secondary" style={{ padding: 16 }}>
                      Not found
                    </Text>
                  )}
                </Card>

                <Card
                  title={`Evaluation (${evaluationId || "N/A"})`}
                  size="small"
                  styles={{ body: { padding: 0 } }}
                >
                  {evaluation ? (
                    <DataDebugger data={evaluation} />
                  ) : (
                    <Text type="secondary" style={{ padding: 16 }}>
                      Not found
                    </Text>
                  )}
                </Card>

                {evaluation?.standoutSummary && (
                  <Card
                    title="standoutSummary (from eval)"
                    size="small"
                    styles={{ body: { padding: 0 } }}
                  >
                    <DataDebugger data={evaluation.standoutSummary} />
                  </Card>
                )}

                <Card
                  title={`Standout Moments (${moments.length})`}
                  size="small"
                  styles={{ body: { padding: 0 } }}
                >
                  {moments.length > 0 ? (
                    <DataDebugger data={moments} />
                  ) : (
                    <Text type="secondary" style={{ padding: 16 }}>
                      No moments found
                    </Text>
                  )}
                </Card>

                <Card
                  title={`Referenced Tags (${referencedTags.length})`}
                  size="small"
                  styles={{ body: { padding: 0 } }}
                >
                  {referencedTags.length > 0 ? (
                    <DataDebugger data={referencedTags} />
                  ) : (
                    <Text type="secondary" style={{ padding: 16 }}>
                      No referenced tags
                    </Text>
                  )}
                </Card>
              </Flex>
            </Col>

            {/* Right column: StandoutMomentPanel */}
            <Col span={10}>
              <Card title="Standout Moments">
                {classDoc && activeStudentId && classDoc.curriculumId ? (
                  <StandoutMomentPanel
                    studentId={activeStudentId}
                    studentName={studentName}
                    semester={classDoc.semester}
                    curriculumId={classDoc.curriculumId}
                    classId={activeClassId || undefined}
                    onSaved={() => void message.success("Moment saved")}
                  />
                ) : (
                  <Text type="secondary">
                    Load a valid student + class to use Quick Tag
                  </Text>
                )}
              </Card>
            </Col>
          </Row>

          <Divider />

          {/* Eval Form Modal */}
          {showEvalModal && evaluationId && (
            <EvaluationFormContextProvider>
              <EvaluationFormModal
                evaluationId={evaluationId}
                onClose={() => setShowEvalModal(false)}
              />
            </EvaluationFormContextProvider>
          )}
        </>
      )}
    </Flex>
  )
}

export default EvalDebugDashboard
